import React, { useEffect, useMemo, useState } from "react";
import {
  defineRole, defineCustomAllocations, getPlan, addPresetRolesBulk,
  deleteRole, updateRole, clearRoleCustom, aiSuggestRoles
} from "../api/postPlansApi";
import {
  Box, Paper, Stack, TextField, MenuItem, Button, Switch, FormControlLabel,
  Typography, Divider, Chip, Alert, IconButton, Drawer, Checkbox
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

export default function StepRoles({ planId, registerSave }) {
  const API_BASE = process.env.REACT_APP_API_BASE || "";

  // ---- preset
  const [preset, setPreset] = useState([]);
  const [pErr, setPErr] = useState(null);
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/post-plans/roles/preset?group=postproduzione`);
        const json = await res.json();
        if (!stop) setPreset(json.roles || []);
      } catch (e) {
        if (!stop) setPErr(e.message);
      }
    })();
    return () => { stop = true; };
  }, [API_BASE]);

  const presetByKey = useMemo(() => {
    const map = new Map();
    for (const r of preset) map.set(r.role_key, r);
    return map;
  }, [preset]);
  const displayLabel = (role_key) => presetByKey.get(role_key)?.label || role_key;

  // ---- lista ruoli + custom
  const [rolesList, setRolesList] = useState([]);
  const [customList, setCustomList] = useState([]);
  const [listErr, setListErr] = useState(null);
  const [listLoading, setListLoading] = useState(true);

  const refreshRoles = async () => {
    try {
      setListLoading(true);
      const p = await getPlan(planId);
      setRolesList(p.roles || []);
      setCustomList(p.custom || []);
      setListErr(null);
    } catch (e) {
      setListErr(e.message);
    } finally {
      setListLoading(false);
    }
  };
  useEffect(() => { if (planId) refreshRoles(); }, [planId]);

  // aggregazioni custom
  const customByRole = useMemo(() => {
    const map = new Map();
    for (const row of customList) {
      const key = row.role_key;
      if (!map.has(key)) map.set(key, { names: [], totalTurns: 0, rows: [] });
      const bucket = map.get(key);
      const name = (row.placeholder_name || "").trim();
      if (name) bucket.names.push(name);
      bucket.totalTurns += Number(row.turns_per_episode || 0);
      bucket.rows.push(row);
    }
    return map;
  }, [customList]);

  // ---- form ruolo
  const [roleKey, setRoleKey] = useState("");
  const [distributionMode, setDistributionMode] = useState("uniform"); // "uniform" | "custom"
  const [turnsDefault, setTurnsDefault] = useState(3);
  const [headcount, setHeadcount] = useState(1);

  // ---- righe custom del form
  const [customRows, setCustomRows] = useState([
    { placeholder_name: "Persona #1", seniority: "junior", turns_per_episode: 3 }
  ]);
  const addCustom = () =>
    setCustomRows([...customRows, { placeholder_name: `Persona #${customRows.length + 1}`, seniority: "junior", turns_per_episode: 3 }]);
  const upd = (i, k, v) => setCustomRows(customRows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const remove = (i) => setCustomRows(customRows.filter((_, idx) => idx !== i));

  const applyPreset = (rk) => { setRoleKey(rk); };

  // Salvataggio (crea/aggiorna)
  const saveRole = async () => {
    if (!roleKey) return true;
    try {
      if (distributionMode === "uniform") {
        const existing = rolesList.find(r => r.role_key === roleKey);
        if (existing) {
          await updateRole(planId, roleKey, {
            distribution_mode: "uniform",
            turns_per_episode_default: Number(turnsDefault),
            headcount: Number(headcount)
          });
        } else {
          await defineRole(planId, {
            role_key: roleKey,
            distribution_mode: "uniform",
            turns_per_episode_default: Number(turnsDefault),
            headcount: Number(headcount)
          });
        }
      } else {
        const existing = rolesList.find(r => r.role_key === roleKey);
        if (!existing) {
          await defineRole(planId, {
            role_key: roleKey,
            distribution_mode: "custom",
            turns_per_episode_default: null,
            headcount: 1
          });
        } else {
          await updateRole(planId, roleKey, { distribution_mode: "custom", turns_per_episode_default: null, headcount: 1 });
        }
        await clearRoleCustom(planId, roleKey);
        await defineCustomAllocations(
          planId,
          customRows.map((r) => ({ role_key: roleKey, ...r }))
        );
      }

      await refreshRoles();
      setRoleKey(""); setDistributionMode("uniform"); setTurnsDefault(3); setHeadcount(1);
      setCustomRows([{ placeholder_name: "Persona #1", seniority: "junior", turns_per_episode: 3 }]);
      return true;
    } catch (e) {
      alert(e.message || "Errore salvataggio ruolo");
      return false;
    }
  };

  // ► blocca l'Avanti se non c'è almeno 1 ruolo salvato
  useEffect(() => {
    if (!registerSave) return;
    registerSave(async () => {
      if (!roleKey && (!rolesList || rolesList.length === 0)) return false;
      return await saveRole();
    });
  }, [registerSave, roleKey, rolesList, distributionMode, turnsDefault, headcount, customRows]);

  const addAllPreset = async () => {
    try {
      const labels = (preset || []).map((p) => p.label);
      await addPresetRolesBulk(planId, labels);
      await refreshRoles();
    } catch (e) {
      alert(e.message || "Errore aggiunta preset");
    }
  };

  // Edit riga: precompila il form
  const onEdit = (row) => {
    setRoleKey(row.role_key);
    if (row.distribution_mode === "uniform") {
      setDistributionMode("uniform");
      setTurnsDefault(Number(row.turns_per_episode_default ?? 0));
      setHeadcount(Number(row.headcount ?? 1));
      setCustomRows([{ placeholder_name: "Persona #1", seniority: "junior", turns_per_episode: 3 }]);
    } else {
      setDistributionMode("custom");
      const agg = customByRole.get(row.role_key);
      const rows = (agg?.rows || []).map(r => ({
        placeholder_name: r.placeholder_name || "",
        seniority: r.seniority || "junior",
        turns_per_episode: Number(r.turns_per_episode || 0)
      }));
      setCustomRows(rows.length ? rows : [{ placeholder_name: "Persona #1", seniority: "junior", turns_per_episode: 3 }]);
      setTurnsDefault(0);
      setHeadcount(1);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Eliminare il ruolo “${displayLabel(row.role_key)}”?`)) return;
    try {
      await deleteRole(planId, row.role_key);
      await refreshRoles();
    } catch (e) {
      alert(e.message || "Errore eliminazione ruolo");
    }
  };

  /* =========================
   * AI Assist (suggest roles)
   * =======================*/
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiList, setAiList] = useState([]); // [{role_key, turns_per_episode_default, headcount, notes, checked}]

  const openAiAssist = async () => {
    try {
      setAiOpen(true);
      setAiLoading(true);
      const res = await aiSuggestRoles(planId, {});
      const list = (res.suggestions || []).map(s => ({ ...s, checked: true }));
      setAiList(list);
    } catch (e) {
      alert(e.message || "Errore AI");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiSuggestions = async () => {
  try {
    for (const s of aiList.filter(x => x.checked)) {
      const exists = rolesList.find(r => r.role_key === s.role_key);
      if (exists) {
        await updateRole(planId, s.role_key, {
          distribution_mode: "uniform",
          turns_per_episode_default: Number(s.turns_per_episode_default || 1),
          headcount: Number(s.headcount || 1)
        });
      } else {
        await defineRole(planId, {
          role_key: s.role_key,
          distribution_mode: "uniform",
          turns_per_episode_default: Number(s.turns_per_episode_default || 1),
          headcount: Number(s.headcount || 1)
        });
      }
    }

    await refreshRoles();
    setAiOpen(false);
  } catch (e) {
    alert(e.message || "Errore applicazione suggerimenti");
  }
};



  return (
    <Box>
      {/* Preset */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>Preset (Postproduzione)</Typography>
          {pErr && <Alert severity="error" sx={{ mb: 1 }}>{pErr}</Alert>}
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
            {preset.map((r) => (
              <Chip
                key={r.role_key}
                label={r.label}
                onClick={() => applyPreset(r.role_key)}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={addAllPreset} disabled={!preset.length}>
              Aggiungi tutti i ruoli di Postproduzione
            </Button>
            <Button size="small" variant="contained" onClick={openAiAssist}>
              AI Assist
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Form ruolo */}
      <Paper variant="outlined">
        <Box p={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              select
              label="Ruolo (da preset)"
              value={roleKey}
              onChange={(e) => applyPreset(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {preset.map((r) => (
                <MenuItem key={r.role_key} value={r.role_key}>
                  {r.label} ({r.role_key})
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={distributionMode === "custom"}
                  onChange={(e) => setDistributionMode(e.target.checked ? "custom" : "uniform")}
                />
              }
              label={distributionMode === "custom" ? "Turni: personalizzati" : "Turni: uniformi"}
            />

            {distributionMode === "uniform" && (
              <>
                <TextField
                  type="number"
                  label="Turni/puntata"
                  value={turnsDefault}
                  onChange={(e) => setTurnsDefault(Number(e.target.value))}
                  sx={{ width: 160 }}
                />
                <TextField
                  type="number"
                  label="Numero persone"
                  value={headcount}
                  onChange={(e) => setHeadcount(Math.max(1, Number(e.target.value) || 1))}
                  sx={{ width: 180 }}
                />
              </>
            )}
          </Stack>

          {distributionMode === "custom" && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Allocazioni previste</Typography>
              {customRows.map((r, i) => (
                <Stack
                  key={i}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems="center"
                  mb={1}
                >
                  <TextField
                    size="small"
                    label="Placeholder/Nome"
                    value={r.placeholder_name}
                    onChange={(e) => upd(i, "placeholder_name", e.target.value)}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    size="small"
                    select
                    label="Seniority"
                    value={r.seniority}
                    onChange={(e) => upd(i, "seniority", e.target.value)}
                    sx={{ width: 160 }}
                  >
                    <MenuItem value="junior">junior</MenuItem>
                    <MenuItem value="senior">senior</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    type="number"
                    label="Turni/puntata"
                    value={r.turns_per_episode}
                    onChange={(e) => upd(i, "turns_per_episode", Number(e.target.value))}
                    sx={{ width: 160 }}
                  />
                  <Button onClick={() => remove(i)}>Rimuovi</Button>
                </Stack>
              ))}
              <Button onClick={addCustom}>Aggiungi riga</Button>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" disabled={!roleKey} onClick={saveRole}>
              {rolesList.find(r => r.role_key === roleKey) ? "Aggiorna" : "Salva"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Lista ruoli del piano */}
      <Paper variant="outlined">
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>Ruoli del piano</Typography>
          {listErr && <Alert severity="error" sx={{ mb: 1 }}>{listErr}</Alert>}
          {listLoading ? (
            <Box>Caricamento…</Box>
          ) : rolesList.length === 0 ? (
            <Box>Nessun ruolo ancora definito.</Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: 8 }}>Ruolo</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Turni/puntata</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Persone</th>
                    <th style={{ textAlign: "right", padding: 8, width: 120 }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {rolesList.map((r) => {
                    const customAgg = customByRole.get(r.role_key);
                    const customNames = customAgg?.names || [];
                    const customTotal = customAgg?.totalTurns || 0;

                    return (
                      <tr key={`${r.role_key}-${r.id}`}>
                        <td style={{ padding: 8 }}>
                          <div>{displayLabel(r.role_key)}</div>
                          {r.distribution_mode === "custom" && customNames.length > 0 && (
                            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                              {customNames.join(", ")}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: 8 }}>
                          {r.distribution_mode === "uniform"
                            ? (r.turns_per_episode_default ?? 0)
                            : (
                              <span style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: 999,
                                fontSize: 12,
                                border: "1px solid rgba(0,0,0,0.2)"
                              }}>
                                personalizzati{customTotal ? ` (${customTotal})` : ""}
                              </span>
                            )
                          }
                        </td>
                        <td style={{ padding: 8 }}>
                          {r.distribution_mode === "uniform" ? (r.headcount ?? 1) : 1}
                        </td>
                        <td style={{ padding: 8, textAlign: "right", whiteSpace: "nowrap" }}>
                          <IconButton size="small" onClick={() => onEdit(r)} title="Modifica"><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => onDelete(r)} title="Elimina"><DeleteIcon fontSize="small" /></IconButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Drawer AI */}
      <Drawer anchor="right" open={aiOpen} onClose={()=>setAiOpen(false)}>
        <Box sx={{ width: 360, p: 2 }}>
          <Typography variant="h6" gutterBottom>AI Assist – Suggerimenti ruoli</Typography>
          {aiLoading ? (
            <Box>Calcolo…</Box>
          ) : aiList.length === 0 ? (
            <Box>Nessun suggerimento.</Box>
          ) : (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {aiList.map((s, i) => (
                <Paper key={i} variant="outlined">
                  <Box p={1.5} display="flex" gap={1} alignItems="center">
                    <Checkbox checked={!!s.checked} onChange={(e)=> {
                      const v = e.target.checked; setAiList(list=> list.map((x,idx)=> idx===i? {...x, checked:v}: x));
                    }} />
                    <Box flex={1}>
                      <Typography variant="subtitle2">
                        {displayLabel(s.role_key)} <Typography component="span" variant="caption" sx={{ opacity: .6 }}>({s.role_key})</Typography>
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: .8 }}>{s.notes || ""}</Typography>
                    </Box>
                  </Box>
                  <Box px={1.5} pb={1.5} display="flex" gap={1}>
                    <TextField
                      size="small" type="number" label="Turni/puntata"
                      value={s.turns_per_episode_default ?? 1}
                      onChange={(e)=>{
                        const v=Number(e.target.value||0);
                        setAiList(list=> list.map((x,idx)=> idx===i? {...x, turns_per_episode_default:v}: x));
                      }}
                      sx={{ width: 140 }}
                    />
                    <TextField
                      size="small" type="number" label="Persone"
                      value={s.headcount ?? 1}
                      onChange={(e)=>{
                        const v=Number(e.target.value||1);
                        setAiList(list=> list.map((x,idx)=> idx===i? {...x, headcount:v}: x));
                      }}
                      sx={{ width: 120 }}
                    />
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={()=>setAiOpen(false)}>Chiudi</Button>
            <Button variant="contained" onClick={applyAiSuggestions} disabled={aiLoading || aiList.filter(x=>x.checked).length===0}>
              Applica
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
